import React, { PropsWithChildren, useContext } from 'react'

import { Features } from 'generated/featureDefinitions'
import { ExpensesRootPageQuery } from 'generated/resources/graphql'

interface ExpensesProviderProps {
  data: ExpensesRootPageQuery
}

interface ExpensesContextType extends ExpensesProviderProps {
  featureAvailabilities: {
    physicalCards: boolean
    singleUseCards: boolean
    virutalCards: boolean
    expensableGroups: boolean
    taxAgencyHomologation: boolean
    reimburseThroughPayroll: boolean
    reimburseThroughSepa: boolean
    reportOnBehalf: boolean
    expensesPerUserPricing: boolean
    canUseSpendingPolicies: boolean
    canUsePerDiems: boolean
    canCreateExpenses: boolean
    compensationsEnabled: boolean
    costCentersFeature: boolean
    projectsManagement: boolean
    hasWorkflowSpendingPolicies: boolean
    hasCostCentersFeature: boolean
    dataCollectionEnabled: boolean
    packagingPaywallsEnabled: boolean
    expensesV2AddonEnabled: boolean
    expensesMultiAccountsEnabled: boolean
    integrationsFrameworkExpensesSync: boolean
  }
}

const ExpensesContext = React.createContext<ExpensesContextType | null>(null)

const featureAvailabilitiesAvailable = (data: ExpensesRootPageQuery, feature: string) => {
  return data.companies.featureAvailabilities.find((f) => f.id === feature)?.available ?? false
}

export const ExpensesContextProvider: React.FC<PropsWithChildren<ExpensesProviderProps>> = ({
  data,
  children,
}) => {
  // Remove after migration to new packaging
  const expensesSubscription = data.expenses.expensesSubscriptionsConnection.nodes[0]

  const canUseExpensableGroups =
    data?.expenses?.expensableGroupsSettingsConnection?.nodes[0]?.enabled ?? false

  const packagingPaywallsEnabled =
    data.features.featuresConnection.nodes.some(
      (el) => el.name === 'dev_packaging_paywalls' && el.enabled
    ) ?? false

  const expensesV2AddonEnabled =
    data.features.featuresConnection.nodes.some(
      (el) => el.name === 'dev_expenses_v2_addon' && el.enabled
    ) ?? false

  const canUseVirtualCardsFeature = packagingPaywallsEnabled
    ? true
    : // Remove after migration to new packaging
      (expensesV2AddonEnabled && expensesSubscription?.canCreateVirtualCards) ?? false

  const canUseSingleUseCardsFeature = packagingPaywallsEnabled
    ? true
    : // Remove after migration to new packaging
      (expensesV2AddonEnabled && expensesSubscription?.canCreateSingleUseCards) ?? false

  const canUsePhysicalCardsFeature = packagingPaywallsEnabled
    ? featureAvailabilitiesAvailable(data, Features.expenses_physical_cards)
    : // Remove after migration to new packaging
      (expensesV2AddonEnabled && expensesSubscription?.canCreatePhysicalCards) ?? false

  const expensesMultiAccountsEnabled =
    data.features.featuresConnection.nodes.some(
      (el) => el.name === 'dev_expenses_multi_accounts' && el.enabled
    ) ?? false

  const canReportOnBehalf = featureAvailabilitiesAvailable(data, Features.expenses_report_on_behalf)

  const canTaxAgencyHomologation = featureAvailabilitiesAvailable(
    data,
    Features.expenses_homologation
  )

  const canUsePerDiems = featureAvailabilitiesAvailable(data, Features.expenses_per_diems)

  const canCreateExpenses = data.expenses._policies.expenseCreateExpenses.can

  const canReimburseThroughPayroll =
    data.expenses._policies.expenseSendToReimburseThroughPayroll.can

  const canReimburseThroughSepa = data.expenses._policies.expenseSendToReimburseThroughPayments.can

  const expensesPerUserPricingAvailable = featureAvailabilitiesAvailable(
    data,
    Features.expenses_per_user_pricing_create_expenses
  )

  const canUseSpendingPolicies = featureAvailabilitiesAvailable(
    data,
    Features.expenses_spending_policies
  )

  const hasWorkflowSpendingPolicies =
    data.features.featuresConnection.nodes.some(
      (el) => el.name === 'dev_expenses_policies_with_workflows' && el.enabled
    ) ?? false

  const costCentersFeature =
    data.features.featuresConnection.nodes.some(
      (el) => el.name === 'dev_cost_centers_v2' && el.enabled
    ) ?? false

  const projectsManagement = featureAvailabilitiesAvailable(data, Features.projects_management)

  const compensationsEnabled = featureAvailabilitiesAvailable(
    data,
    Features.compensations_management
  )

  const dataCollectionEnabled =
    data.features.featuresConnection.nodes.some(
      (el) => el.name === 'dev_expenses_data_collection' && el.enabled
    ) ?? false

  const integrationsFrameworkExpensesSync =
    data.features.featuresConnection.nodes.some(
      (el) => el.name === 'dev_expenses_integrations_framework_sync' && el.enabled
    ) ?? false

  return (
    <ExpensesContext.Provider
      value={{
        data,
        featureAvailabilities: {
          expensesMultiAccountsEnabled: expensesMultiAccountsEnabled,
          physicalCards: canUsePhysicalCardsFeature,
          singleUseCards: canUseSingleUseCardsFeature,
          virutalCards: canUseVirtualCardsFeature,
          expensableGroups: canUseExpensableGroups,
          taxAgencyHomologation: canTaxAgencyHomologation,
          reimburseThroughPayroll: canReimburseThroughPayroll,
          reimburseThroughSepa: canReimburseThroughSepa,
          reportOnBehalf: canReportOnBehalf,
          expensesPerUserPricing: expensesPerUserPricingAvailable,
          canUseSpendingPolicies: canUseSpendingPolicies,
          canUsePerDiems: canUsePerDiems,
          canCreateExpenses: canCreateExpenses,
          compensationsEnabled: compensationsEnabled,
          costCentersFeature: costCentersFeature,
          projectsManagement: projectsManagement,
          hasWorkflowSpendingPolicies: hasWorkflowSpendingPolicies,
          hasCostCentersFeature: costCentersFeature,
          dataCollectionEnabled: dataCollectionEnabled,
          integrationsFrameworkExpensesSync: integrationsFrameworkExpensesSync,
          packagingPaywallsEnabled: packagingPaywallsEnabled,
          expensesV2AddonEnabled: expensesV2AddonEnabled,
        },
      }}
    >
      {children}
    </ExpensesContext.Provider>
  )
}

export const useExpenses = () => {
  const context = useContext(ExpensesContext)

  if (!context) {
    throw new Error('useExpenses used without a provider')
  }

  return context
}
